// Fabric AI consumption notice - a one-time sheet for owners and admins of
// existing organisations.  G360_K634
//
// Mounted once for the whole console (ConsoleApp, beside the consent
// banners). It asks console_notice whether this person should see it; it
// never renders for anyone but an owner or admin, never when the notice RPC
// is not switched on, and never blocks work: it is a non-modal sheet at the
// top of the page. Escape or the X hides it for this browser session only
// (nothing is recorded); the three buttons record the person's answer.
//
// G360_K636 - who sees it is decided on the server (console_notice, 0329):
// owners and admins of organisations created before 2026-10-01 that have a
// connected Microsoft app, while the Fabric setup checks do not all pass,
// unless they snoozed it or anyone in the organisation said "We don't use
// Fabric". Buttons: Set up now (one script) - records setup_started and opens
// the drawer at the script; Remind me in 7 days; We don't use Fabric (asks to
// confirm; hides it for the whole organisation). "Acknowledge" was removed.
//
// G360_K637 - the server (console_notice, 0331) now hides it as soon as
// Fabric data works through the Azure connection OR the Govern360 app
// (fabric_status), so customers with the Azure connection do not see it
// once the daily sync has read their capacities. Set up now opens the
// drawer at its summary (no longer at the script).
import { useEffect, useRef, useState } from 'react';
import { X, Sparkles } from 'lucide-react';
import { useAuth } from '@govern360/shared';
import { NOTICE_KEY, readNotice, ackNotice } from '../lib/fabricApi.js';
import FabricSetupDrawer from './FabricSetupDrawer.jsx';

const SESSION_KEY = 'g360_notice_hidden_' + NOTICE_KEY;
function hiddenThisSession() { try { return window.sessionStorage.getItem(SESSION_KEY) === '1'; } catch (e) { return false; } }
function hideThisSession() { try { window.sessionStorage.setItem(SESSION_KEY, '1'); } catch (e) { /* private window: hidden until reload */ } }

export default function FabricNotice() {
  const { tenant, activeRole } = useAuth();
  const tid = (tenant && tenant.id) || null;
  const admin = activeRole === 'owner' || activeRole === 'admin';
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState(null);
  const [setup, setSetup] = useState(false);
  // G360_K636 - "We don't use Fabric" asks to confirm first
  const [confirmNotUsed, setConfirmNotUsed] = useState(false);
  const titleRef = useRef(null);

  useEffect(() => {
    setShow(false);
    if (!tid || !admin || hiddenThisSession()) return undefined;
    let live = true;
    readNotice(tid, NOTICE_KEY).then((r) => { if (live && !r.missing && !r.error && r.data && r.data.show) setShow(true); });
    return () => { live = false; };
  }, [tid, admin]);
  useEffect(() => {
    if (!show) return undefined;
    if (titleRef.current) titleRef.current.focus();
    const onKey = (e) => { if (e.key === 'Escape' && !setup) { hideThisSession(); setShow(false); } };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [show, setup]);

  async function answer(action) {
    setBusy(action); setErr(null);
    const r = await ackNotice(tid, NOTICE_KEY, action, 7);
    setBusy(null);
    if (!r.ok) { setErr(r.error); return false; }
    setShow(false); setConfirmNotUsed(false);
    return true;
  }
  async function startSetup() {
    await answer('setup_started');
    setShow(false); setSetup(true);
  }

  return (
    <>
      {/* G360_K634 */}
      {show && (
        <div role="dialog" aria-modal="false" aria-labelledby="fb-notice-title" data-testid="fb-notice"
          style={{ position: 'fixed', top: 12, left: '50%', transform: 'translateX(-50%)', width: 'min(620px, calc(100vw - 32px))', zIndex: 150,
            background: 'var(--g-surface)', border: '1px solid var(--g-divider)', borderTop: '3px solid #1a73e8', borderRadius: 12, boxShadow: '0 10px 30px rgba(15,23,42,.22)', padding: '16px 18px' }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <Sparkles size={18} style={{ color: '#1a73e8', marginTop: 2 }} />
            <h2 id="fb-notice-title" ref={titleRef} tabIndex={-1} style={{ flex: 1, fontSize: 15.5, fontWeight: 600, margin: 0, outline: 'none' }}>Microsoft is changing how Fabric AI consumption is measured</h2>
            <button type="button" aria-label="Hide for now" data-testid="fb-notice-x" onClick={() => { hideThisSession(); setShow(false); }}
              style={{ border: 0, background: 'transparent', cursor: 'pointer', color: 'var(--g-text-3)', padding: 2 }}><X size={16} /></button>
          </div>
          <div data-testid="fb-notice-body" style={{ fontSize: 12.5, color: 'var(--g-text-2)', lineHeight: 1.6, marginTop: 8 }}>
            <p style={{ margin: '0 0 6px' }}>
              Microsoft is moving Fabric AI experiences to a consumption model that measures the work each task needs, in phases.
              Ontology AI experiences switch first, from October 2026. Copilot in Fabric, data agents, operations agents, AI Functions,
              Real-Time Intelligence and more follow later this year; Microsoft will confirm those dates.
            </p>
            <p style={{ margin: '0 0 6px' }}>
              AI use is still measured and billed in Fabric Capacity Units (CUs), as today. How many CUs a task uses will vary with the model and
              reasoning, the context, and the tools and orchestration involved, so the same work can cost more or less than before.
            </p>
            <p style={{ margin: 0 }}>
              What Govern360 does: records a baseline for each AI workload before it switches, compares before and after, attributes CUs to each
              capacity, workspace and item (and, with Microsoft's optional preview feed, to the person or agent), and alerts on step changes.
            </p>
            {/* G360_K636 */}
            {/* G360_K637 */}
            <p data-testid="fb-notice-auto" style={{ margin: '6px 0 0' }}>It disappears on its own once Govern360 reads Fabric - through your Azure connection or the Govern360 app.</p>
          </div>
          {err && <div data-testid="fb-notice-err" style={{ fontSize: 12, color: 'var(--g-warning)', marginTop: 8 }}>{err}</div>}
          {/* G360_K636 - Set up now (one script) / Remind me in 7 days / We don't use Fabric; Acknowledge removed */}
          {!confirmNotUsed && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
              <button type="button" className="btn primary" data-testid="fb-notice-setup" disabled={!!busy} onClick={startSetup}>{busy === 'setup_started' ? 'Opening...' : 'Set up now'}</button>
              <button type="button" className="btn outline" data-testid="fb-notice-snooze" disabled={!!busy} onClick={() => answer('snoozed')}>{busy === 'snoozed' ? 'Saving...' : 'Remind me in 7 days'}</button>
              <button type="button" className="btn outline" data-testid="fb-notice-notused" disabled={!!busy} onClick={() => { setErr(null); setConfirmNotUsed(true); }}>We don't use Fabric</button>
            </div>
          )}
          {confirmNotUsed && (
            <div data-testid="fb-notice-confirm" role="alertdialog" aria-label="Confirm" style={{ marginTop: 12, padding: '10px 12px', border: '1px solid var(--g-divider)', borderRadius: 8, background: 'var(--g-bg-soft)' }}>
              <div style={{ fontSize: 12.5, color: 'var(--g-text)', lineHeight: 1.55 }}>
                Hide this notice for everyone in your organisation, for good? Choose this only if your organisation does not use Microsoft Fabric.
                You can still set Fabric up later from Copilot Consumption, Fabric.
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                <button type="button" className="btn primary" data-testid="fb-notice-notused-yes" disabled={!!busy} onClick={() => answer('not_used')}>{busy === 'not_used' ? 'Saving...' : "Yes, we don't use Fabric"}</button>
                <button type="button" className="btn outline" data-testid="fb-notice-notused-no" disabled={!!busy} onClick={() => setConfirmNotUsed(false)}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}
      {/* G360_K636 - opens at the one script */}
      {/* G360_K637 - opens at the summary */}
      {admin && tid && setup && <FabricSetupDrawer open={setup} onClose={() => setSetup(false)} tenantId={tid} />}
    </>
  );
}
